/* eslint-disable max-lines -- File grew past 400 lines due to required useCallback/useMemo hoists for react-perf rules; splitting would fragment shared state closure. */
import {
  CharacterFlow,
  NotificationType,
  useApp,
  useAvatarImage,
  useAvatarImageModels,
  useAvatarPersistedSettings,
  useAvatarPromptStyles,
  useTranslation,
} from "@next-platform/client-common-core";
import {
  CapabilityGate,
  SubCapabilityKey,
  useApplicationCapabilitiesAllowed,
} from "@next-platform/client-common-core/infra/application/capabilities";
import { Button, ButtonSize, ButtonVariant, Modal, ModalSize, useMedia } from "@next-platform/client-common-spicy-ui";
import { AvatarModelCapability, AvatarPromptFormat, IDisplayableAvatarImageModel } from "@next-platform/packages-ts-types";
import { ChangeEvent, JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { ImageGallery, ModelSelector, PromptImageStyleSelector } from "@/components/AvatarGeneratorModal/components";
import CapabilityAdvancedOptions from "@/components/AvatarGeneratorModal/components/CapabilityAdvancedOptions";
import CapabilityEditImageModal from "@/components/AvatarGeneratorModal/components/CapabilityEditImageModal";
import { PromptSection } from "@/components/AvatarGeneratorModal/components/PromptSection";
import { CONTAINER_GAP, IMAGE_WIDTH, REQUIRED_FIELDS_FOR_SUGGEST_IMAGE_PROMPT } from "@/components/AvatarGeneratorModal/constants";
import { useAvatarGeneration } from "@/components/AvatarGeneratorModal/hooks/useAvatarGeneration";
import type { AvatarGeneratedImage, IAvatarGeneratorModalProps } from "@/components/AvatarGeneratorModal/types";
import { AvatarGeneratedImageType } from "@/components/AvatarGeneratorModal/types";
import { Row, Text } from "@/components/customComponents";
import { DOM_EVENTS, KEYBOARD_KEYS } from "@/constants/dom";
import { ROUTES } from "@/constants/navigation";
import { useFieldsSuggestion } from "@/hooks/useFieldsSuggestion";
import useToast from "@/hooks/useToast";
import { SUGGESTION_FIELDS } from "@/pages/CreateCharacter/v2/constants";
import { useImageScrolling } from "@/pages/CreateCharacter/v2/hooks";
import { cn } from "@/utils";

import Trans from "../Trans/Trans";
import GenerateImageButton from "./GenerateImageButton";
/* eslint-disable complexity,max-lines-per-function -- TODO: Refactor this component to reduce complexity */
function AvatarGeneratorModal({
  isVisible,
  currentAvatarUrl,
  onClose,
  onSave,
  form,
  formType,
  isAiPromptEnabled = true,
}: IAvatarGeneratorModalProps): JSX.Element {
  // Persisted settings (web: localStorage; native: AsyncStorage). Resolves stale ids and
  // permission-revoked models to the current app-config default.
  const { modelStyle, setModelStyle, promptImageStyle, setPromptImageStyle } = useAvatarPersistedSettings();

  // Session state (not persisted)
  const [statePrompt, setStatePrompt] = useState("");
  const [stateNegativePrompt, setStateNegativePrompt] = useState("");
  const [stateGuidanceImage, setStateGuidanceImage] = useState<string | ArrayBuffer | null>(null);
  const [stateFixedSeed, setStateFixedSeed] = useState("");

  const [stateIsSeedLocked, setStateIsSeedLocked] = useState(false);
  const [stateGeneratedImages, setStateGeneratedImages] = useState<AvatarGeneratedImage[]>([]);
  const [stateCurrentImageIndex, setStateCurrentImageIndex] = useState(0);
  const [stateAbortController, setStateAbortController] = useState<AbortController | null>(null);

  // UI state
  const [stateAvatarUrl, setStateAvatarUrl] = useState(currentAvatarUrl);

  const [stateIsBlobLoading, setStateIsBlobLoading] = useState(false);

  const [stateEditParentImageIndex, setStateEditParentImageIndex] = useState<number | null>(null);
  const [stateEditInstruction, setStateEditInstruction] = useState("");

  const [stateIsEditModalOpen, setStateIsEditModalOpen] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const isImageGenerationSettingsAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.ChatImageImageGenerationSettings);

  // Edit flow reuses whichever model Contentful advertises as the image-to-image default
  // for this app. The business-logic parser guarantees this references a model that has
  // the `"reference_image"` capability, or is `""` when no such model exists.
  const { avatarImageGeneration } = useApp();
  const [stateIsPromptEmpty, setStateIsPromptEmpty] = useState(false);
  const editModelId = avatarImageGeneration.editModelId;
  const avatarPromptEmptyError = stateIsPromptEmpty ? t("chatbot:validation.field.required") : "";
  // Model + style lists sourced from app config (Contentful) via business-logic hooks.
  // `modelsToDisplay` already has `isDisabled` computed per-entry based on the viewer's
  // permissions, so the modal renders locked models disabled rather than hiding them.
  const { modelsToDisplay: imageModels, modelsById, shouldShowSelector: shouldShowModelSelector } = useAvatarImageModels();
  const { promptStylesToDisplay: promptImageStyles, shouldShowSelector: shouldShowStyleSelector } = useAvatarPromptStyles();

  // Feature availability keys off `modelStyle` (the resolved persisted selection or the
  // config-authored `defaultModelId`), NOT off `imageModels.length`. An app may intentionally
  // omit the selector (empty `models`) while still providing a fixed `defaultModelId` for
  // generation — that should remain functional. Unavailable only when nothing resolves.
  const isAvatarGenerationAvailable = modelStyle !== "";

  // Routes the user to the subscribe page when they click a visible-but-locked model.
  const handleDisabledModelClick = useCallback(() => {
    navigate(ROUTES.subscribe);
  }, [navigate]);

  // Derive per-model capability flags from `capabilities` tokens. Capabilities come from the
  // single authoritative source — the selected model's entry in `models`. Selector-less apps
  // must still include the model in the `models` array (with a single entry); the selector UI
  // is then hidden by `shouldShowModelSelector` (`models.length < 2`).
  const selectedModel: IDisplayableAvatarImageModel | undefined = modelsById[modelStyle];
  const hasPromptStylesSupport = selectedModel?.capabilities.includes(AvatarModelCapability.PromptStyles) ?? false;
  const hasNegativePromptSupport = selectedModel?.capabilities.includes(AvatarModelCapability.NegativePrompt) ?? false;
  const hasReferenceImageSupport = selectedModel?.capabilities.includes(AvatarModelCapability.ReferenceImage) ?? false;
  const promptFormat = selectedModel?.promptFormat ?? AvatarPromptFormat.Keywords;
  const referenceImageUrl = useMemo(() => {
    const parentImage = stateEditParentImageIndex !== null ? stateGeneratedImages[stateEditParentImageIndex] : undefined;
    return parentImage ? `data:${parentImage.type};base64,${parentImage.base64}` : undefined;
  }, [stateEditParentImageIndex, stateGeneratedImages]);
  const editInstructionLabelValues = useMemo(
    () => ({
      instruction: stateGeneratedImages[stateCurrentImageIndex]?.editInstruction ?? "",
    }),
    [stateCurrentImageIndex, stateGeneratedImages],
  );

  const { isDesktop } = useMedia();
  const { displayToast } = useToast();

  const isRequiredFieldsFilled = useMemo(() => {
    const requiredFields = REQUIRED_FIELDS_FOR_SUGGEST_IMAGE_PROMPT[formType ?? ""] || [];
    return requiredFields.every((fieldName: string) => form?.fields?.[fieldName]?.isValid);
  }, [form?.fields, formType]);

  const { handleGenerateSuggestField, activeGeneratingField } = useFieldsSuggestion({
    form,
    formType,
    onFieldValueChange: setStatePrompt,
  });

  const { errorMessage, errorType, loadingType, onUserEditAvatarImage } = useAvatarImage();
  const isError = !!(errorMessage && errorType === CharacterFlow.GENERATE_AVATAR_IMAGE);
  const isEditError = !!(errorMessage && errorType === CharacterFlow.EDIT_AVATAR_IMAGE);
  const isAvatarGenerating = loadingType === CharacterFlow.GENERATE_AVATAR_IMAGE;
  const isAvatarSaving = loadingType === CharacterFlow.SAVE_AVATAR_IMAGE;
  const isAvatarEditing = loadingType === CharacterFlow.EDIT_AVATAR_IMAGE;

  const handleGeneratedImageSelect = useCallback((selectedImage: AvatarGeneratedImage, index: number) => {
    setStateCurrentImageIndex(index);

    if (selectedImage.generationType === AvatarGeneratedImageType.GENERATION) {
      setStatePrompt(selectedImage.prompt);
      setStateNegativePrompt(selectedImage.negativePrompt);
    }

    setStateAvatarUrl(`data:${selectedImage?.type};base64,${selectedImage?.base64}`);
    setStateFixedSeed(selectedImage?.seed?.toString());
  }, []);

  const { isDragging, navigateToNextImage, navigateToPrevImage, scrollToImage, scrollToStart, containerProps } = useImageScrolling({
    generatedImages: stateGeneratedImages,
    currentImageIndex: stateCurrentImageIndex,
    isAvatarGenerating,
    onImageSelect: handleGeneratedImageSelect,
    isVisible,
    imageWidth: IMAGE_WIDTH,
    containerGap: CONTAINER_GAP,
  });

  const handleImageSelect = useCallback(
    (index: number) => {
      const selectedImage = stateGeneratedImages[index];
      if (selectedImage) {
        handleGeneratedImageSelect(selectedImage, index);
      }
    },
    [stateGeneratedImages, handleGeneratedImageSelect],
  );

  const handleImageCrop = useCallback((index: number, croppedBase64: string) => {
    setStateGeneratedImages((prev: AvatarGeneratedImage[]) => {
      const originalImage = prev[index];
      // Keeps the original's generationRef: cropping reframes the pixels but does not change which
      // prompt and model produced them, which is what the reference records.
      const croppedImage: AvatarGeneratedImage = {
        ...originalImage,
        id: uuidv4(),
        base64: croppedBase64,
      };
      return [croppedImage, ...prev];
    });
    setStateCurrentImageIndex(0);
  }, []);

  const handleImageDelete = useCallback(
    (index: number) => {
      setStateGeneratedImages((prev: AvatarGeneratedImage[]) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
      if (stateCurrentImageIndex >= index && stateCurrentImageIndex > 0) {
        setStateCurrentImageIndex((prev: number) => prev - 1);
      }
    },
    [stateCurrentImageIndex],
  );

  const handleEditImage = useCallback(
    async (instruction: string, parentImageIndex: number): Promise<boolean> => {
      const parentImage = stateGeneratedImages[parentImageIndex];
      if (!parentImage) return false;

      const abortController = new AbortController();
      setStateAbortController(abortController);

      try {
        const { data: editResult, error } = await onUserEditAvatarImage({
          prompt: instruction,
          model: editModelId,
          referenceImage: `data:${parentImage.type};base64,${parentImage.base64}`,
          abortSignal: abortController.signal,
        });

        if (error || !editResult) {
          return false;
        }
        const editedImage: AvatarGeneratedImage = {
          id: uuidv4(),
          base64: editResult.base64,
          type: editResult.type,
          seed: editResult.seed,
          prompt: instruction,
          negativePrompt: "",
          generationType: AvatarGeneratedImageType.EDIT,
          editInstruction: instruction,
          parentImageId: parentImage.id,
          generationRef: editResult.generation_ref,
        };

        setStateGeneratedImages((prev: AvatarGeneratedImage[]) => [editedImage, ...prev]);
        setStateCurrentImageIndex(0);
        scrollToStart();
        setStateAvatarUrl(`data:${editedImage.type};base64,${editedImage.base64}`);
        return true;
      } finally {
        setStateAbortController(null);
      }
    },
    [
      editModelId,
      stateGeneratedImages,
      onUserEditAvatarImage,
      scrollToStart,
      setStateAvatarUrl,
      setStateCurrentImageIndex,
      setStateGeneratedImages,
      setStateAbortController,
    ],
  );

  const handleImageEdit = useCallback((index: number) => {
    setStateEditParentImageIndex(index);
    setStateEditInstruction("");
    setStateIsEditModalOpen(true);
  }, []);

  useEffect(() => {
    if (!hasReferenceImageSupport && stateGuidanceImage) {
      setStateGuidanceImage(null);
    }
  }, [hasReferenceImageSupport, stateGuidanceImage]);

  // Business logic hook
  const { handleSave, handleGenerate, cancelGeneration, handleImageUpload } = useAvatarGeneration({
    prompt: statePrompt,
    negativePrompt: stateNegativePrompt,
    guidanceImage: stateGuidanceImage,
    fixedSeed: stateFixedSeed,
    seedLocked: stateIsSeedLocked,
    modelStyle,
    promptImageStyle,
    supportsPromptStyles: hasPromptStylesSupport,
    supportsNegativePrompt: hasNegativePromptSupport,
    supportsReferenceImage: hasReferenceImageSupport,
    generatedImages: stateGeneratedImages,
    currentImageIndex: stateCurrentImageIndex,
    setAvatarUrl: setStateAvatarUrl,
    setFixedSeed: setStateFixedSeed,
    setGeneratedImages: setStateGeneratedImages,
    setCurrentImageIndex: setStateCurrentImageIndex,
    setAbortController: setStateAbortController,
    setGuidanceImage: setStateGuidanceImage,
    setIsBlobLoading: setStateIsBlobLoading,
    onSave,
    scrollToStart,
    scrollToImage,
  });

  const refFileInput = useRef<HTMLInputElement | null>(null);

  const isGenerateButtonVisuallyDisabled = !statePrompt || !isAvatarGenerationAvailable;

  const handleGenerateButtonClick = useCallback(() => {
    if (!isAvatarGenerationAvailable) {
      return;
    }
    if (!statePrompt.trim()) {
      setStateIsPromptEmpty(true);
      return;
    }
    void handleGenerate();
  }, [handleGenerate, isAvatarGenerationAvailable, statePrompt, t]);

  // Keyboard shortcut: Ctrl+Enter to generate image
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === KEYBOARD_KEYS.ENTER) {
        e.preventDefault();
        if (isAvatarGenerating) {
          return;
        }
        handleGenerateButtonClick();
      }
    };

    if (isVisible) {
      window.addEventListener(DOM_EVENTS.KEYDOWN, handleKeyDown);
      return () => window.removeEventListener(DOM_EVENTS.KEYDOWN, handleKeyDown);
    }
  }, [isVisible, isAvatarGenerating, handleGenerateButtonClick]);

  useEffect(() => {
    return () => {
      if (stateAvatarUrl) {
        URL.revokeObjectURL(stateAvatarUrl);
      }
    };
  }, [stateAvatarUrl]);

  const onImageUpload = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      handleImageUpload(file);
    },
    [handleImageUpload],
  );

  const handlePromptChange = useCallback(
    (value: string) => {
      if (stateIsPromptEmpty) setStateIsPromptEmpty(false);
      setStatePrompt(value);
    },
    [stateIsPromptEmpty],
  );

  const handleImageUploadButtonClick = useCallback(() => {
    refFileInput.current?.click();
  }, []);

  const handleCancelGeneration = useCallback(() => cancelGeneration(stateAbortController), [cancelGeneration, stateAbortController]);

  const isSaveVisuallyDisabled = !stateGeneratedImages.length;

  const handleSaveClick = useCallback(() => {
    if (!stateGeneratedImages.length) {
      displayToast({
        message: t("error:noImageToSave"),
        type: NotificationType.error,
      });
      return;
    }
    void handleSave();
  }, [displayToast, handleSave, stateGeneratedImages.length, t]);

  const handleEditModalCancel = useCallback(() => {
    if (stateAbortController) {
      stateAbortController.abort();
      setStateAbortController(null);
    }
    setStateIsEditModalOpen(false);
    setStateEditParentImageIndex(null);
    setStateEditInstruction("");
  }, [stateAbortController]);

  const handleEditModalGenerate = useCallback(async () => {
    if (stateEditParentImageIndex !== null && stateEditInstruction.trim()) {
      const isSuccess = await handleEditImage(stateEditInstruction, stateEditParentImageIndex);
      if (isSuccess) {
        setStateIsEditModalOpen(false);
        setStateEditParentImageIndex(null);
        setStateEditInstruction("");
      }
    }
  }, [handleEditImage, stateEditInstruction, stateEditParentImageIndex]);

  const handleEditModalGenerateClick = useCallback(() => {
    void handleEditModalGenerate();
  }, [handleEditModalGenerate]);

  const handleEditModalCancelGeneration = useCallback(() => {
    if (stateAbortController) {
      stateAbortController.abort();
      setStateAbortController(null);
    }
  }, [stateAbortController]);

  const handleGenerateSuggestFieldForAvatar = useCallback(
    () => handleGenerateSuggestField(SUGGESTION_FIELDS.AVATAR_PROMPT),
    [handleGenerateSuggestField],
  );

  const handleRemoveGuidanceImage = useCallback(() => setStateGuidanceImage(null), []);

  const handleSeedLockToggle = useCallback(() => setStateIsSeedLocked((prev) => !prev), []);

  const modalClassNames = useMemo(() => ({ content: "gap-[12px]" }), []);

  // Center to the viewport, ignoring the side nav. The base Modal already centers itself with
  // `left-1/2 -translate-x-1/2`; adding a side-nav-dependent left margin shifted it off-center
  // relative to the full window (NX-10876). Sizing-only overrides keep the viewport centering intact.
  const modalClassName = useMemo(() => {
    return cn(isDesktop && "max-w-4xl max-h-[95vh] rounded-lg");
  }, [isDesktop]);

  return (
    <Modal
      size={ModalSize.FULLSCREEN}
      title={t("common:modal.generateAvatar.title")}
      open={isVisible}
      onOpenChange={onClose}
      classNames={modalClassNames}
      className={modalClassName}
      actions={
        <Row justify="end" className="gap-2">
          <GenerateImageButton
            isGenerationInProgress={isAvatarGenerating}
            onCancelGeneration={handleCancelGeneration}
            onClick={handleGenerateButtonClick}
            isVisuallyDisabled={isGenerateButtonVisuallyDisabled}
            disabled={isAvatarSaving || stateIsBlobLoading}
          />
          <Button
            variant={ButtonVariant.CONTAINED}
            onClick={handleSaveClick}
            size={ButtonSize.MEDIUM}
            disabled={isError || stateGeneratedImages[stateCurrentImageIndex]?.isNsfw}
            loading={isAvatarSaving || stateIsBlobLoading}
            aria-disabled={isSaveVisuallyDisabled}
            className={`transition-all ${isSaveVisuallyDisabled ? "opacity-50 cursor-not-allowed" : "enabled:hover:opacity-80"}`}
          >
            <Trans i18nKey="common:modal.generateAvatar.cta.saveImage" />
          </Button>
        </Row>
      }
    >
      <ImageGallery
        generatedImages={stateGeneratedImages}
        currentImageIndex={stateCurrentImageIndex}
        isAvatarGenerating={isAvatarGenerating}
        isDragging={isDragging}
        containerProps={containerProps}
        prompt={statePrompt}
        modelStyle={modelStyle}
        onNavigatePrev={navigateToPrevImage}
        onNavigateNext={navigateToNextImage}
        onImageSelect={handleImageSelect}
        onImageEdit={editModelId && isImageGenerationSettingsAllowed ? handleImageEdit : undefined}
        onImageCrop={handleImageCrop}
        onImageDelete={handleImageDelete}
      />

      <CapabilityEditImageModal
        open={stateIsEditModalOpen}
        instruction={stateEditInstruction}
        isGenerating={isAvatarEditing}
        referenceImageUrl={referenceImageUrl}
        errorMessage={isEditError ? errorMessage : null}
        onInstructionChange={setStateEditInstruction}
        onCancel={handleEditModalCancel}
        onGenerate={handleEditModalGenerateClick}
        onCancelGeneration={handleEditModalCancelGeneration}
      />

      {stateGeneratedImages[stateCurrentImageIndex]?.generationType === AvatarGeneratedImageType.EDIT ? (
        <CapabilityGate capability={SubCapabilityKey.ChatImageImageGenerationSettings}>
          <Text className="text-sm text-gray-11">
            <Trans i18nKey="common:modal.generateAvatar.editInstruction.label" values={editInstructionLabelValues} />
          </Text>
        </CapabilityGate>
      ) : null}

      <PromptSection
        prompt={statePrompt}
        isAiPromptEnabled={isAiPromptEnabled}
        isRequiredFieldsFilled={isRequiredFieldsFilled}
        activeGeneratingField={activeGeneratingField}
        suggestionField={SUGGESTION_FIELDS.AVATAR_PROMPT}
        promptFormat={promptFormat}
        onPromptChange={handlePromptChange}
        onGenerateSuggestField={handleGenerateSuggestFieldForAvatar}
        errorMessage={avatarPromptEmptyError}
      />

      {!isAvatarGenerationAvailable && (
        <Text className="text-sm text-gray-11 text-center py-4">
          <Trans i18nKey="common:modal.generateAvatar.unavailable" />
        </Text>
      )}

      {shouldShowModelSelector ? (
        <ModelSelector
          imageModels={imageModels}
          modelStyle={modelStyle}
          onModelChange={setModelStyle}
          onDisabledModelClick={handleDisabledModelClick}
        />
      ) : null}

      {hasPromptStylesSupport && shouldShowStyleSelector ? (
        <PromptImageStyleSelector promptImageStyle={promptImageStyle} promptImageStyles={promptImageStyles} onStyleChange={setPromptImageStyle} />
      ) : null}

      <CapabilityAdvancedOptions
        supportsNegativePrompt={hasNegativePromptSupport}
        supportsReferenceImage={hasReferenceImageSupport}
        negativePrompt={stateNegativePrompt}
        guidanceImage={stateGuidanceImage}
        fixedSeed={stateFixedSeed}
        seedLocked={stateIsSeedLocked}
        generatedImagesCount={stateGeneratedImages.length}
        fileInputRef={refFileInput}
        onNegativePromptChange={setStateNegativePrompt}
        onRemoveGuidanceImage={handleRemoveGuidanceImage}
        onImageUploadClick={handleImageUploadButtonClick}
        onImageUpload={onImageUpload}
        onSeedLockToggle={handleSeedLockToggle}
      />

      {errorMessage ? (
        <Text type="error" className="text-center">
          {errorMessage}
        </Text>
      ) : null}
    </Modal>
  );
}

export default AvatarGeneratorModal;
